import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  useTheme,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { grey } from "@mui/material/colors";
import { ImageModal } from "./components";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const AppTable = ({ ariaLabel, columns, rows, onEdit, onDelete, onClick }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImageClick = (imageUrl, event) => {
    event.stopPropagation();
    setSelectedImage(imageUrl);
    setOpenImageModal(true);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label={ariaLabel}>
        <TableHead sx={{ backgroundColor: "primary.main" }}>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || "left"}
                sx={{ color: "white", fontWeight: "bold" }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : rows
          ).map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              hover
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                backgroundColor: rowIndex % 2 === 0 ? "inherit" : grey[100],
              }}
              onClick={() => onClick && onClick(row)}
            >
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align || "left"}>
                  {column.id === "action" ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      {onEdit !== undefined ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          style={{ height: 30 }}
                          startIcon={<EditIcon />}
                          onClick={(e) => {
                            onEdit(row);
                            e.stopPropagation();
                          }}
                        >
                          Edit
                        </Button>
                      ) : (
                        <></>
                      )}

                      {onDelete !== undefined ? (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          style={{ height: 30, marginLeft: 10 }}
                          startIcon={<DeleteIcon />}
                          onClick={(e) => {
                            onDelete(row);
                            e.stopPropagation();
                          }}
                        >
                          Delete
                        </Button>
                      ) : (
                        <></>
                      )}
                      {onClick !== undefined ? (
                        <Button
                          size="small"
                          sx={{
                            height: 30,
                            marginLeft: 1,
                          }}
                          onClick={(e) => {
                            onClick(row);

                            e.stopPropagation();
                          }}
                        >
                          <ChevronRightIcon />
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Box>
                  ) : column.id === "check_in_image_url" ||
                    column.id === "check_out_image_url" ? (
                    row[column.id] === "No Image" ? (
                      row[column.id]
                    ) : (
                      <img
                        src={row[column.id]}
                        alt={`${column.id} for ${row.id}`}
                        style={{ width: 50, height: 50, cursor: "pointer" }}
                        onClick={(e) => handleImageClick(row[column.id], e)}
                      />
                    )
                  ) : (
                    row[column.id]
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={columns.length} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={columns.length}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <ImageModal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        imageUrl={selectedImage}
      />
    </TableContainer>
  );
};

AppTable.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "right", "center"]),
    })
  ).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AppTable;
